import {
  AdminApiError,
  type AdminClient,
  type GreedykingCategoryBonusOverride,
} from '@apx/admin-sdk';
import { prettyGame } from '../games.js';
import { askPrompt } from '../modal.js';
import { fmtDateTime } from '../format.js';
import { greedykingCategoryBonusSchema } from '@apx/integrator-config/schemas';
import { showToast } from '../toast.js';

export interface CategoryBonusPageDeps {
  client: AdminClient;
  integratorId: string;
  gameId: string;
}

const DEFAULT_VALUE: GreedykingCategoryBonusOverride = { veg: 0.2, meat: 0.3 };

export async function renderCategoryBonusPage(
  root: HTMLElement,
  deps: CategoryBonusPageDeps,
): Promise<void> {
  root.innerHTML = `
    <h1 class="page-title">Category Bonus Rates — ${prettyGame(deps.gameId)}</h1>
    <p class="page-sub">Top-up bonus applied to a winning primary match, computed as <code>basePayout × categoryBonus[category]</code>. Defaults: veg 20%, meat 30%.</p>

    <div class="card">
      <h2>Rates</h2>
      <div class="row" style="gap:24px">
        <label style="flex:1">Veg bonus (0..2)
          <input id="cb-veg" type="number" min="0" max="2" step="0.01" />
          <span class="muted" id="cb-veg-pct">— %</span>
        </label>
        <label style="flex:1">Meat bonus (0..2)
          <input id="cb-meat" type="number" min="0" max="2" step="0.01" />
          <span class="muted" id="cb-meat-pct">— %</span>
        </label>
      </div>
      <div class="row" style="margin-top:14px">
        <button id="cb-save" class="primary">Propose change</button>
        <button id="cb-reset" class="ghost">Reset to defaults (20% / 30%)</button>
      </div>
      <p id="cb-msg" class="muted"></p>
    </div>

    <div class="card">
      <h2>Pending approvals for this surface</h2>
      <div id="cb-pending"><p class="muted">Loading…</p></div>
    </div>
  `;

  const vegEl = root.querySelector<HTMLInputElement>('#cb-veg')!;
  const meatEl = root.querySelector<HTMLInputElement>('#cb-meat')!;
  const vegPctEl = root.querySelector<HTMLElement>('#cb-veg-pct')!;
  const meatPctEl = root.querySelector<HTMLElement>('#cb-meat-pct')!;
  const msgEl = root.querySelector<HTMLElement>('#cb-msg')!;

  function updatePcts(): void {
    vegPctEl.textContent = `${(Number(vegEl.value || 0) * 100).toFixed(1)} %`;
    meatPctEl.textContent = `${(Number(meatEl.value || 0) * 100).toFixed(1)} %`;
  }
  vegEl.addEventListener('input', updatePcts);
  meatEl.addEventListener('input', updatePcts);

  function fillForm(value: GreedykingCategoryBonusOverride): void {
    vegEl.value = String(value.veg);
    meatEl.value = String(value.meat);
    updatePcts();
  }

  const { value: current } = await deps.client.getSurface(
    deps.integratorId,
    deps.gameId,
    'categoryBonus',
  );
  fillForm((current as GreedykingCategoryBonusOverride | null) ?? DEFAULT_VALUE);

  root.querySelector<HTMLButtonElement>('#cb-reset')!.addEventListener('click', () => {
    fillForm(DEFAULT_VALUE);
    msgEl.textContent = 'Reset to defaults (not saved).';
    msgEl.className = 'muted';
  });

  root.querySelector<HTMLButtonElement>('#cb-save')!.addEventListener('click', async () => {
    msgEl.textContent = '';
    const value: GreedykingCategoryBonusOverride = {
      veg: Number(vegEl.value),
      meat: Number(meatEl.value),
    };
    const parsed = greedykingCategoryBonusSchema.safeParse(value);
    if (!parsed.success) {
      msgEl.textContent = parsed.error.errors.map((e) => `${e.path.join('.')}: ${e.message}`).join(' • ');
      msgEl.className = 'error';
      return;
    }
    try {
      const res = await deps.client.proposeSurface(
        deps.integratorId,
        deps.gameId,
        'categoryBonus',
        parsed.data,
      );
      msgEl.textContent = `Pending approval ${res.approval.id}.`;
      msgEl.className = 'muted';
      await refreshPending();
    } catch (err) {
      msgEl.textContent = err instanceof AdminApiError ? `${err.code}: ${err.message}` : String(err);
      msgEl.className = 'error';
    }
  });

  async function refreshPending(): Promise<void> {
    const all = await deps.client.listApprovals(deps.integratorId, 'pending');
    const subset = all.filter((a) => a.surface === 'categoryBonus' && a.gameId === deps.gameId);
    const container = root.querySelector<HTMLElement>('#cb-pending')!;
    if (subset.length === 0) {
      container.innerHTML = '<p class="muted">No pending changes.</p>';
      return;
    }
    container.innerHTML = `<table>
      <thead><tr><th>Created</th><th>Proposer</th><th>Pending</th><th></th></tr></thead>
      <tbody>${subset
        .map(
          (a) => `<tr>
            <td>${fmtDateTime(a.createdAt)}</td>
            <td><code>${a.proposerUserId.slice(0, 8)}…</code></td>
            <td><code>${JSON.stringify(a.pendingConfig)}</code></td>
            <td>
              <button data-approve="${a.id}">Approve</button>
              <button data-reject="${a.id}" class="ghost">Reject</button>
            </td>
          </tr>`,
        )
        .join('')}</tbody>
    </table>`;
    container.querySelectorAll<HTMLButtonElement>('button[data-approve]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        try {
          await deps.client.approve(btn.dataset.approve!);
          await refreshPending();
          const fresh = await deps.client.getSurface(
            deps.integratorId,
            deps.gameId,
            'categoryBonus',
          );
          fillForm((fresh.value as GreedykingCategoryBonusOverride | null) ?? DEFAULT_VALUE);
        } catch (err) {
          showToast(err instanceof AdminApiError ? `${err.code}: ${err.message}` : String(err), 'error');
        }
      });
    });
    container.querySelectorAll<HTMLButtonElement>('button[data-reject]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const reason = await askPrompt('Rejection reason?');
        if (!reason) return;
        try {
          await deps.client.reject(btn.dataset.reject!, reason);
          await refreshPending();
        } catch (err) {
          showToast(err instanceof AdminApiError ? `${err.code}: ${err.message}` : String(err), 'error');
        }
      });
    });
  }

  await refreshPending();
}
